import React, { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";

import { IconName } from "../types";
import Icon from "./Icon";

let timeout: number;
const Carousel = (props: {
  onSlide: (index: number) => void;
  onSlideEnd?: (index: number) => void;
  currentSlideIndex?: number;
  stepSize?: number;
  hidePagination?: boolean;
  numItems?: number;
  itemWidthPerc: number; // Width of viewport each item should be, as a percentage
  spacingPx: number; // Spacing between items, in pixels
  items: JSX.Element[];
}) => {
  const [numItems, setNumItems] = useState(
    props.numItems !== undefined ? props.numItems : 0
  );
  const [itemWidthPerc, setItemWidthPerc] = useState(props.itemWidthPerc ?? 0);
  const [activeIndex, setActiveIndex] = useState(
    props.currentSlideIndex !== undefined ? props.currentSlideIndex : 0
  );
  const [carouselWidthPerc, setCarouselWidthPerc] = useState(0);

  const swipeHandlers = useSwipeable({
    delta: 10,
    preventDefaultTouchmoveEvent: false,
    trackTouch: true,
    trackMouse: true,
    rotationAngle: 0,
    onSwiped: (eventData) => {
      if (eventData.dir === "Left") {
        onClickNext();
      } else if (eventData.dir === "Right") {
        onClickPrev();
      }
    },
  });

  useEffect(() => {
    // When the component first mounts, expand the carousel width to contain
    // all items next to each other (width = numItems * 100%), there may be extra space, but that is fine.
    // See next useEffect() for the next width calculations
    const _numItems = props.items.length;
    setNumItems(_numItems);
    setCarouselWidthPerc(_numItems * 100);
  }, []);

  useEffect(() => {
    const _numItems = props.items.length;
    if (_numItems != numItems) {
      setNumItems(_numItems);
      setCarouselWidthPerc(_numItems * 100);
    }
  }, [props.items]);

  useEffect(() => {
    if (props.currentSlideIndex !== undefined) {
      setActiveIndex(props.currentSlideIndex);
    }
  }, [props.currentSlideIndex]);

  useEffect(() => {
    setItemWidthPerc(props.itemWidthPerc);
  }, [props.itemWidthPerc]);

  const onClickPrev = () => {
    if (canClickPrev()) {
      updateCurrentIndex((activeIndex - Math.floor(props.stepSize ?? 1)) | 0);
    }
  };
  const onClickNext = () => {
    if (canClickNext()) {
      updateCurrentIndex((activeIndex + Math.floor(props.stepSize ?? 1)) | 0);
    }
  };

  const canClickPrev = () => {
    return activeIndex > 0;
  };
  const canClickNext = () => {
    // Stop when we have all items visible on the screen
    // How many can we fit on screen at once
    const numVisible = 100 / itemWidthPerc;
    // How many are left off screen?
    const numRemaining = numItems - (activeIndex + numVisible);
    return numRemaining > 0;
  };

  const updateCurrentIndex = (newIndex: number) => {
    setActiveIndex(newIndex);
    props.onSlide(newIndex);

    if (timeout) {
      clearTimeout(timeout);
    }
    // @ts-ignore
    timeout = setTimeout(function () {
      if (props.onSlideEnd) {
        props.onSlideEnd(newIndex);
      }
    }, 600); // transition duration defined in carousel.scss
  };

  const getTranslateX = () => {
    return `calc(${-((activeIndex * itemWidthPerc) / numItems)}%)`;
  };

  return (
    <div className={`pyht-carousel num-items-${numItems}`}>
      <div className={"pyht-carousel-inner"} {...swipeHandlers}>
        <div
          className={"pyht-carousel-items"}
          style={{
            width: carouselWidthPerc > 0 ? carouselWidthPerc + "%" : "auto",
            transform: `translateX(${getTranslateX()})`,
          }}
        >
          {props.items.map((item, index) => {
            let width = itemWidthPerc + "%";
            if (carouselWidthPerc > 0) {
              width = (itemWidthPerc / carouselWidthPerc) * 100 + "%";
            }
            let paddingRight = props.spacingPx + "px";
            if (
              props.stepSize &&
              props.stepSize > 1 &&
              (index + 1) % props.stepSize === 0
            ) {
              paddingRight = "0";
            }

            return (
              <div
                key={index}
                style={{
                  width,
                  paddingRight,
                }}
                className={"pyht-carousel-item"}
              >
                {item}
              </div>
            );
          })}
        </div>
        <div
          className={`pyht-carousel-next ${!canClickNext() ? "hidden" : ""}`}
          role={"button"}
          onClick={() => onClickNext()}
        >
          <Icon name={IconName.ARROW_THIN} />
        </div>
        <div
          className={`pyht-carousel-prev ${!canClickPrev() ? "hidden" : ""}`}
          role={"button"}
          onClick={() => onClickPrev()}
        >
          <Icon name={IconName.ARROW_THIN} />
        </div>
      </div>
      {!props.hidePagination && numItems > (props.stepSize || 1) && (
        <div className={"pyht-carousel-pagination"}>
          {[...Array(numItems)].map((_, index) => {
            return (
              <div
                key={`item-${index}`}
                className={`pyht-carousel-pagination-item ${
                  activeIndex === index ? "active" : ""
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Carousel;
